import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import instance from '../../instance';
import CallNotification from '../../context/NotificationContext';
import {INomenclatureGroup} from '../../interfaces/fetch/Nomenclature';
import {ConfirmDialog} from '../../ui/ConfirmDialog';
import {pluralize} from '../../functionts/pluralize';
import {Tooltip} from '@material-ui/core';

interface Props {
    open: boolean;
    onClose: () => void;
    userId: number | null;
    onChange?: () => void;
}

export const NomenclatureGroupsManagerPopup = ({open, onClose, userId, onChange}: Props) => {
    const {handleErrorNotification} = CallNotification();
    const [allGroups, setAllGroups] = useState<INomenclatureGroup[]>([]);
    const [filteredGroups, setFilteredGroups] = useState<INomenclatureGroup[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState<string>('');

    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newName, setNewName] = useState<string>('');

    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title?: string;
        content: React.ReactNode;
        confirmText?: string;
        confirmColor?: string;
        onConfirm: () => void | Promise<void>;
    }>({
        open: false,
        content: null,
        confirmText: 'Подтвердить',
        confirmColor: undefined,
        onConfirm: () => {},
    });

    const theme = useTheme();

    const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
    const [deletingIds, setDeletingIds] = useState<number[]>([]);
    const [isAddingGroup, setIsAddingGroup] = useState<boolean>(false);

    const fetchGroups = useCallback(async () => {
        if (!userId) return;
        setIsLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'GetNomenclatureGroups',
                params: {
                    user_id: Number(userId),
                    sort: [{column_name: 'ng.name', direction: 'ASC'}],
                },
            });
            if (!response.data.error) {
                setAllGroups(response.data.nomenclature_groups || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (open) {
            // Сбрасываем состояния при открытии
            setSearchText('');
            setEditingId(null);
            setIsAdding(false);
            setNewName('');
            setIsSavingEdit(false);
            setDeletingIds([]);
            setIsAddingGroup(false);
            void fetchGroups();
        }
    }, [open, fetchGroups]);

    // Отдельный эффект для поиска, чтобы он срабатывал при изменении searchText
    useEffect(() => {
        if (searchText) {
            const filtered = allGroups.filter(group => group.name.toLowerCase().includes(searchText.toLowerCase()));
            setFilteredGroups(filtered);
        } else {
            setFilteredGroups(allGroups);
        }
    }, [searchText, allGroups]);

    const handleSaveEdit = async (id: number | null) => {
        if (!userId || !editName.trim()) return;
        setIsSavingEdit(true);
        try {
            const response = await instance.post('/LK', {
                method: 'EditNomenclatureGroup',
                params: {
                    user_id: Number(userId),
                    nomenclature_group_id: id,
                    nomenclature_group_name: editName,
                },
            });
            handleErrorNotification(response, true);
            if (!response.data.error) {
                setEditingId(null);
                setEditName('');
                void fetchGroups();
                if (onChange) onChange();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDelete = async (id: number | null) => {
        if (!userId) return;
        setDeletingIds(prev => [...prev, id as number]);
        const response = await instance.post('/LK', {
            method: 'GetNomenclatureGroupInfo',
            params: {
                user_id: Number(userId),
                nomenclature_group_id: id,
            },
        });
        const nomenclatureGroupInfo = response?.data?.nomenclature_group_info;
        setDeletingIds(prev => prev.filter(item => item !== (id as number)));
        setConfirmState({
            open: true,
            title: 'Подтверждение удаления',
            content: nomenclatureGroupInfo?.has_items ? (
                <Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
                        <Typography sx={{color: 'warning.main'}}>
                            В группе {pluralize(nomenclatureGroupInfo.items_count, 'позиция', 'позиции', 'позиций', true)} номенклатуры.
                            <br />
                            При удалении позиции номенклатуры останутся без группы.
                        </Typography>
                    </Box>
                    <Typography>Вы уверены, что хотите удалить эту группу?</Typography>
                </Box>
            ) : (
                'Вы уверены, что хотите удалить эту группу?'
            ),
            confirmText: 'Удалить',
            confirmColor: theme.palette.error.dark,
            onConfirm: async () => {
                setDeletingIds(prev => [...prev, id as number]);
                setConfirmState(prev => ({...prev, open: false}));
                try {
                    const response = await instance.post('/LK', {
                        method: 'DeleteNomenclatureGroup',
                        params: {
                            user_id: Number(userId),
                            nomenclature_group_id: id,
                        },
                    });
                    handleErrorNotification(response, true);
                    if (!response.data.error) {
                        // Сначала обновляем локальное состояние
                        setAllGroups(prev => prev.filter(g => g.id !== id));
                        // Затем перезагружаем с сервера для синхронизации
                        await fetchGroups();
                        if (onChange) onChange();
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    setDeletingIds(prev => prev.filter(item => item !== (id as number)));
                }
            },
        });
    };

    const handleAdd = async () => {
        if (!userId || !newName.trim()) return;
        setIsAddingGroup(true);
        try {
            const response = await instance.post('/LK', {
                method: 'CreateNomenclatureGroup',
                params: {
                    user_id: Number(userId),
                    nomenclature_group_name: newName,
                },
            });
            handleErrorNotification(response, true);
            if (!response.data.error) {
                setIsAdding(false);
                setNewName('');
                void fetchGroups();
                if (onChange) onChange();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsAddingGroup(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Box
                sx={{
                    p: '20px 24px 10px 3px',
                    position: 'relative',
                    backgroundColor: '#f5f7ff',
                }}
            >
                <DialogTitle sx={{pr: 5}}>Редактирование групп номенклатуры</DialogTitle>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent>
                <Box display="flex" gap={1} mb={2} alignItems="center" justifyContent="space-between">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAdding(true)}
                        disabled={isAdding}
                        sx={{minWidth: '130px'}}
                    >
                        Создать
                    </Button>
                    <TextField
                        size="small"
                        placeholder="Поиск групп"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon color="action" />,
                        }}
                    />
                </Box>

                {isAdding && (
                    <Box display="flex" gap={1} mb={2} alignItems="center" bgcolor="#f5f5f5" p={1} borderRadius={2}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Название новой группы"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            autoFocus
                        />
                        <Tooltip title="Сохранить">
                            <IconButton color="primary" onClick={() => void handleAdd()} disabled={isAddingGroup}>
                                {isAddingGroup ? <CircularProgress size={24} /> : <SaveOutlinedIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Отменить">
                            <IconButton onClick={() => setIsAdding(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {isLoading && !filteredGroups.length ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List sx={{maxHeight: '400px', overflow: 'auto'}}>
                        {filteredGroups.map(group => (
                            <ListItem
                                key={group.id}
                                //divider
                                sx={{display: 'flex', alignItems: 'center', flexDirection: 'row'}}
                            >
                                {editingId === group.id ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') void handleSaveEdit(group.id);
                                        }}
                                        sx={{marginLeft: -2}}
                                    />
                                ) : (
                                    <ListItemText primary={group.name} />
                                )}
                                <Box sx={{display: 'flex', flexDirection: 'row', gap: 1.5, ml: 1.5}}>
                                    {editingId === group.id ? (
                                        <>
                                            <Tooltip title="Сохранить">
                                                <IconButton
                                                    edge="end"
                                                    color="primary"
                                                    onClick={() => void handleSaveEdit(group.id)}
                                                    disabled={isSavingEdit}
                                                >
                                                    {isSavingEdit ? <CircularProgress size={24} /> : <SaveOutlinedIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Отменить">
                                                <IconButton edge="end" onClick={() => setEditingId(null)}>
                                                    <CloseIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            <Tooltip title="Редактировать">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => {
                                                        setEditingId(group.id);
                                                        setEditName(group.name);
                                                    }}
                                                >
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Удалить">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => void handleDelete(group.id)}
                                                    disabled={deletingIds.includes(group.id as number)}
                                                >
                                                    {deletingIds.includes(group.id as number) ? (
                                                        <CircularProgress size={24} />
                                                    ) : (
                                                        <DeleteOutlineIcon />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>
                            </ListItem>
                        ))}
                        {filteredGroups.length === 0 && !isLoading && (
                            <Typography align="center" color="textSecondary" mt={2}>
                                {searchText ? 'Ничего не найдено' : 'Список групп пуст'}
                            </Typography>
                        )}
                    </List>
                )}
            </DialogContent>
            <ConfirmDialog
                open={confirmState.open}
                onClose={() => setConfirmState(prev => ({...prev, open: false}))}
                title={confirmState.title}
                content={confirmState.content}
                confirmText={confirmState.confirmText}
                confirmColor={confirmState.confirmColor}
                onConfirm={confirmState.onConfirm}
                isLoading={deletingIds.length > 0}
            />
        </Dialog>
    );
};
